// frontend/src/components/WorkshopQuotationDetail.js
// Read-only devis viewer for the workshop side — chrome/ink, French.
// Lines the client declined are struck through and excluded from the total,
// mirroring how the backend recomputes a partially-accepted devis.

import React from 'react';
import { Modal, Badge, Table, Tr, Td, Button } from './ui/Iparc';
import { fmt } from '../theme/iparc';

const STATUS = {
  pending:   { label: 'En attente', tone: 'warn' },
  revised:   { label: 'Révisé',     tone: 'warn' },
  countered: { label: 'Contre-offre', tone: 'warn' },
  accepted:  { label: 'Accepté',    tone: 'ok' },
  confirmed: { label: 'Confirmé',   tone: 'ok' },
  rejected:  { label: 'Refusé',     tone: 'danger' },
};

const Line = ({ label, value, tone }) => (
  <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
    <span className="text-[#8F877B]">{label}</span>
    <span
      className="font-['Space_Grotesk',sans-serif] tabular-nums"
      style={{ color: tone || '#F4F1EA' }}
    >
      {value}
    </span>
  </div>
);

export default function WorkshopQuotationDetail({ quotation, onClose }) {
  if (!quotation) return null;

  const items = quotation.items || [];
  const discountPct = Number(quotation.discountPercent) || 0;
  const clientSubtotalFull = items.reduce(
    (s, it) => s + (Number(it.clientPrice || it.price) || 0) * (Number(it.quantity) || 1),
    0
  );
  const discountAmount = discountPct > 0 ? Math.round(clientSubtotalFull * discountPct / 100) : 0;
  const subtotalDisplay = discountPct > 0
    ? Math.round(clientSubtotalFull)
    : (quotation.clientSubtotal || quotation.subtotal || quotation.total || 0);
  const tax = Number(quotation.tax) || 0;
  const total = quotation.clientTotal || quotation.total || 0;
  const deposit = Number(quotation.depositAmount) || 0;
  const st = STATUS[quotation.status] || { label: quotation.status || '—', tone: 'neutral' };
  const declinedCount = items.filter((it) => it.accepted === false).length;

  return (
    <Modal
      open
      onClose={onClose}
      width="max-w-3xl"
      subtitle={`Devis ${quotation.documentNumber || ''}`.trim()}
      title={
        <>
          {quotation.client?.name || 'Client'}
          <span className="text-[#6E665C] font-normal"> · </span>
          <span className="text-[#C9C3B6] font-normal text-[14px]">
            {quotation.vehicle?.brand || ''} {quotation.vehicle?.model || ''}
            {quotation.vehicle?.plateNumber ? ` — ${quotation.vehicle.plateNumber}` : ''}
          </span>
        </>
      }
      footer={<Button onClick={onClose}>Fermer</Button>}
    >
      <div className="flex items-center gap-2.5 flex-wrap">
        <Badge tone={st.tone}>{st.label}</Badge>
        {quotation.partiallyAccepted && <Badge tone="warn">Partiellement accepté</Badge>}
        {/* The reading the devis was quoted at — the car has moved since. */}
        {quotation.odometerReading > 0 && (
          <span className="text-[11px] text-[#6E665C] font-['Space_Grotesk',sans-serif] tabular-nums">
            {fmt(quotation.odometerReading)} km au devis
          </span>
        )}
        {quotation.confirmedBy?.role && (
          <span className="text-[11px] text-[#6E665C]">
            Confirmé par&nbsp;
            {quotation.confirmedBy.role === 'workshop'
              ? "l'atelier"
              : quotation.confirmedBy.role === 'admin' ? "l'administration" : 'le client'}
          </span>
        )}
      </div>

      <div className="rounded-xl border border-[#241F1B] overflow-hidden -mx-0.5">
        <Table
          head={['Catégorie', 'Désignation', 'Réf', 'Marque', 'Qté', 'P.U.', 'Prix client']}
          minWidth={720}
        >
          {items.map((it, i) => {
            const declined = it.accepted === false;
            return (
              <Tr key={i} className={declined ? 'opacity-45' : ''}>
                <Td className="text-[#8F877B]">{it.category || '—'}</Td>
                <Td className={declined ? 'line-through' : 'text-[#F4F1EA]'}>
                  {it.description || '—'}
                </Td>
                <Td className="text-[#8F877B]">{it.reference || '—'}</Td>
                <Td className="text-[#8F877B]">{it.brand || '—'}</Td>
                <Td mono className="text-center">{it.quantity || 1}</Td>
                <Td mono className="text-right">{fmt(it.price)}</Td>
                <Td mono className="text-right">
                  {fmt((it.clientPrice || it.price) * (it.quantity || 1))}
                </Td>
              </Tr>
            );
          })}
        </Table>
      </div>

      {declinedCount > 0 && (
        <p className="text-[11.5px] text-[#DFA23C]">
          {declinedCount} ligne{declinedCount > 1 ? 's' : ''} refusée
          {declinedCount > 1 ? 's' : ''} par le client — exclue
          {declinedCount > 1 ? 's' : ''} du total.
        </p>
      )}

      <div className="ml-auto w-full sm:w-[280px] flex flex-col gap-1.5 pt-1">
        <Line label="Sous-total" value={`${fmt(subtotalDisplay)} F`} />
        {discountPct > 0 && (
          <Line label={`Remise ${discountPct} %`} value={`−${fmt(discountAmount)} F`} tone="#74A98D" />
        )}
        {tax > 0 && <Line label="TVA (18 %)" value={`${fmt(tax)} F`} tone="#DFA23C" />}
        <div className="pt-2 mt-1 border-t border-[#241F1B]">
          <div className="flex items-baseline justify-between gap-6">
            <span className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Total</span>
            <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[19px] font-semibold text-[#F4F1EA]">
              {fmt(total)} <span className="text-[12px] text-[#6E665C]">F</span>
            </span>
          </div>
        </div>
        {deposit > 0 && <Line label="Acompte demandé" value={`${fmt(deposit)} F`} />}
      </div>

      {(quotation.note || quotation.clientNote) && (
        <div className="rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3">
          <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1.5">Note</p>
          <p className="text-[12.5px] text-[#C9C3B6] leading-[1.55]">
            {quotation.note || quotation.clientNote}
          </p>
        </div>
      )}
    </Modal>
  );
}
